import React, { ReactNode, useEffect, useRef, useState } from 'react'

interface SliderProps {
  slidesToShowMobile: number
  slidesToShowTablet: number
  slidesToShowDesktop: number
  slidesToScrollMobile?: number
  slidesToScrollTablet?: number
  slidesToScrollDesktop?: number
  autoplay?: boolean
  autoplayDuration?: number
  children?: ReactNode
  dotsVariant?: 'white' | 'green'
  containerStyles?: string
}

const Slider = ({
  slidesToShowMobile,
  slidesToShowTablet,
  slidesToShowDesktop,
  slidesToScrollMobile = 1,
  slidesToScrollTablet = slidesToShowTablet,
  slidesToScrollDesktop = slidesToShowDesktop,
  autoplay = false,
  autoplayDuration = 1000,
  children,
  dotsVariant = 'green',
  containerStyles,
}: SliderProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [slidesToShow, setSlidesToShow] = useState(slidesToShowMobile)
  const [slidesToScroll, setSlidesToScroll] = useState(slidesToScrollMobile)
  const [activeSlide, setActiveSlide] = useState(0)
  const totalSlides = React.Children.count(children)
  const totalDots = Math.ceil(totalSlides / slidesToShow)

  const goToSlide = (index: number) => {
    setActiveSlide(index)
    setCurrentIndex(index * slidesToScroll)
  }

  const handleResize = () => {
    const windowWidth = window.innerWidth
    if (windowWidth >= 1024) {
      setSlidesToShow(slidesToShowDesktop)
      setSlidesToScroll(slidesToScrollDesktop)
    } else if (windowWidth >= 768) {
      setSlidesToShow(slidesToShowTablet)
      setSlidesToScroll(slidesToScrollTablet)
    } else {
      setSlidesToShow(slidesToShowMobile)
      setSlidesToScroll(slidesToScrollMobile)
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)

    let autoplayTimer: NodeJS.Timeout | undefined

    if (autoplay) {
      autoplayTimer = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % totalDots)
        setCurrentIndex((prevIndex) =>
          prevIndex >= totalSlides - slidesToShow
            ? 0
            : prevIndex + slidesToScroll
        )
      }, autoplayDuration)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      if (autoplayTimer) {
        clearInterval(autoplayTimer)
      }
    }
  }, [
    slidesToShowMobile,
    slidesToShowTablet,
    slidesToShowDesktop,
    slidesToShow,
    autoplay,
    totalDots,
    autoplayDuration,
    totalSlides,
  ])

  return (
    <div className="relative w-full overflow-hidden">
      <div
        className={`flex transition-transform duration-300 ease-in justify-center ${containerStyles}`}
      >
        {React.Children.toArray(children ?? []).map((child, index) => {
          if (index >= currentIndex && index < currentIndex + slidesToShow) {
            return child
          }
          return null
        })}
      </div>
      {totalDots > 1 && (
        <div className="flex justify-center items-center mt-10">
          {Array.from({ length: totalDots }, (_, index) => (
            <button
              key={index}
              className={`aspect-square w-2 sm:w-3 rounded-full border ml-2 sm:ml-3 ${
                dotsVariant === 'white' ? 'border-white' : 'border-primary-100'
              } ${
                index === activeSlide
                  ? `${dotsVariant === 'white' ? 'bg-white' : 'bg-primary-100'}`
                  : ''
              }`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Slider
